// InvoiceGenerator.js — TraverseClean Invoice Generator
// Generates monthly invoices per Town of Paradise contract TOP-25-27 requirements
import { useState, useEffect, useCallback } from 'react';
import { BUILDINGS, CONTRACT, SPECIAL_EVENTS_ALLOCATION, SPECIAL_EVENTS_RATE_SHORT, SPECIAL_EVENTS_RATE_FULL, getContractYearStart } from '../../lib/data';
import { supabase } from '../../lib/supabase';
import { formatDate } from '../../lib/utils';
import { format as formatDateFns } from 'date-fns';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const HST_RATE = 0.15;
const CONTRACT_START = { year: 2026, month: 3 };

function getMonthRange(year, month) {
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  return { start, end };
}

// Per the contract, the first 600 special-event hours each contract year
// are already covered by the base monthly rate. Only once cumulative hours
// for the year pass that allocation does an event become billable — and
// then it's billed as a flat per-event rate ($280 short / $520 full-day),
// not per hour. This walks events in date order tracking the running
// year-to-date total so the same event always lands on the same side of
// the threshold no matter which month it's viewed from.
function computeBilledEvents(eventsInMonth, hoursBeforeMonth) {
  let runningTotal = hoursBeforeMonth;
  return eventsInMonth.map(e => {
    const hours = e.hours_worked || 0;
    runningTotal += hours;
    const billable = runningTotal > SPECIAL_EVENTS_ALLOCATION;
    const rate = e.event_type === 'full' ? SPECIAL_EVENTS_RATE_FULL : SPECIAL_EVENTS_RATE_SHORT;
    return { ...e, billable, rate, amount: billable ? rate : 0 };
  });
}

// This app-supplied text (notes, PO number, event/worker names from
// special_event_logs, etc.) gets interpolated straight into HTML that's
// rendered via dangerouslySetInnerHTML, printed, and emailed to the Town.
// Escape it so a stray '<' or quote can't break the layout or inject
// markup into that outgoing email.
function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatCurrency(n) {
  return '$' + n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function generateInvoiceNumber(year, month) {
  const yy = String(year).slice(-2);
  const mm = String(month + 1).padStart(2, '0');
  return `TC-${yy}${mm}-001`;
}

function fromDbRow(row) {
  return {
    id: row.id, month: row.month, year: row.year, monthLabel: row.month_label,
    poNumber: row.po_number, authorizedBy: row.authorized_by,
    buildingSubtotal: row.building_subtotal, specialEventsSubtotal: row.special_events_subtotal,
    totalSpecialHours: row.total_special_hours, subtotal: row.subtotal, hst: row.hst, total: row.total,
    buildingCount: row.building_count, createdAt: row.created_at, status: row.status, notes: row.notes,
    sentAt: row.sent_at, paidAt: row.paid_at,
  };
}

function toDbRow(inv) {
  return {
    id: inv.id, month: inv.month, year: inv.year, month_label: inv.monthLabel,
    po_number: inv.poNumber || null, authorized_by: inv.authorizedBy || null,
    building_subtotal: inv.buildingSubtotal, special_events_subtotal: inv.specialEventsSubtotal,
    total_special_hours: inv.totalSpecialHours, subtotal: inv.subtotal, hst: inv.hst, total: inv.total,
    building_count: inv.buildingCount, status: inv.status, notes: inv.notes || null,
    created_at: inv.createdAt, sent_at: inv.sentAt || null, paid_at: inv.paidAt || null,
  };
}

function getBillableMonths() {
  const now = new Date();
  const months = [];
  let y = CONTRACT_START.year;
  let m = CONTRACT_START.month;
  while (y < now.getFullYear() || (y === now.getFullYear() && m <= now.getMonth())) {
    months.push({ year: y, month: m, label: `${MONTHS[m]} ${y}` });
    m++;
    if (m > 11) { m = 0; y++; }
  }
  return months.reverse();
}

export default function InvoiceGenerator() {
  const billableMonths = getBillableMonths();
  const now = new Date();
  const defaultMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
  const defaultYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();

  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [poNumber, setPoNumber] = useState('');
  const [authorizedBy, setAuthorizedBy] = useState('Jason Lawrence');
  const [specialEventHours, setSpecialEventHours] = useState([]);
  const [hoursBeforeMonth, setHoursBeforeMonth] = useState(0);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [view, setView] = useState('setup');
  const [savedInvoices, setSavedInvoices] = useState([]);
  const [invoicesError, setInvoicesError] = useState(null);
  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const { start: monthStart, end: monthEnd } = getMonthRange(selectedYear, selectedMonth);

  const activeBuildings = BUILDINGS.filter(b => {
    if (b.status === 'inactive') {
      // Compare a single monotonic value (year*12+month) instead of
      // `selectedYear >= 2026 && selectedMonth >= 8` — the old check only
      // matched Sep–Dec of years >= 2026 and dropped Building A again for
      // Jan–Aug of any later year, even though it reopened for good.
      if (b.id === 'A' && (selectedYear * 12 + selectedMonth) >= (2026 * 12 + 8)) return true;
      return false;
    }
    if (b.id === 'M' && (selectedYear > 2026 || (selectedYear === 2026 && selectedMonth >= 3))) return false;
    return true;
  });

  const fetchSpecialEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      // Local-date formatting, not toISOString() — these are local-midnight
      // Date objects, and .toISOString() reads the UTC date, which is only
      // safe for Newfoundland's specific negative offset and would silently
      // misalign the query in a different deployment timezone or around DST.
      const startStr = formatDateFns(monthStart, 'yyyy-MM-dd');
      const endStr = formatDateFns(monthEnd, 'yyyy-MM-dd');
      const yearStartStr = formatDateFns(getContractYearStart(monthStart), 'yyyy-MM-dd');

      const [monthRes, priorRes] = await Promise.all([
        supabase
          .from('special_event_logs')
          .select('*')
          .gte('event_date', startStr)
          .lte('event_date', endStr)
          .order('event_date', { ascending: true }),
        // Only need the hour totals for everything earlier in this
        // contract year, to know how much of the 600hr allocation is
        // already used up before this month's events are considered.
        supabase
          .from('special_event_logs')
          .select('hours_worked')
          .gte('event_date', yearStartStr)
          .lt('event_date', startStr),
      ]);
      if (monthRes.error) throw monthRes.error;
      if (priorRes.error) throw priorRes.error;
      setSpecialEventHours(monthRes.data || []);
      setHoursBeforeMonth((priorRes.data || []).reduce((sum, e) => sum + (e.hours_worked || 0), 0));
    } catch (err) {
      console.error('Failed to fetch special events:', err);
      setSpecialEventHours([]);
      setHoursBeforeMonth(0);
    }
    setLoadingEvents(false);
    // monthStart/monthEnd intentionally excluded: they're new Date objects every
    // render, so including them re-triggers this effect on every render forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth]);

  useEffect(() => { fetchSpecialEvents(); }, [fetchSpecialEvents]);

  const loadInvoices = useCallback(async () => {
    const { data, error } = await supabase.from('invoices').select('*').order('created_at', { ascending: false });
    if (error) {
      setInvoicesError(error.message);
      return;
    }
    setInvoicesError(null);
    setSavedInvoices((data || []).map(fromDbRow));
  }, []);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const buildingSubtotal = activeBuildings.reduce((sum, b) => sum + b.monthlyRate, 0);
  const totalSpecialHours = specialEventHours.reduce((sum, e) => sum + (e.hours_worked || 0), 0);
  const billedEvents = computeBilledEvents(specialEventHours, hoursBeforeMonth);
  const billableEventCount = billedEvents.filter(e => e.billable).length;
  const specialEventsSubtotal = billedEvents.reduce((sum, e) => sum + e.amount, 0);
  const subtotal = buildingSubtotal + specialEventsSubtotal;
  const hst = subtotal * HST_RATE;
  const total = subtotal + hst;
  const invoiceNumber = generateInvoiceNumber(selectedYear, selectedMonth);

  async function saveInvoice(status) {
    const existing = savedInvoices.find(i => i.id === invoiceNumber);
    // Re-opening Setup and clicking "Preview" for a month that was already
    // sent/paid must not silently downgrade it back to 'draft' and erase
    // its sentAt/paidAt audit trail — just leave the saved record alone.
    if (existing && existing.status !== 'draft' && (status || 'draft') === 'draft') {
      return existing;
    }
    const invoice = {
      id: invoiceNumber, month: selectedMonth, year: selectedYear,
      monthLabel: `${MONTHS[selectedMonth]} ${selectedYear}`,
      poNumber, authorizedBy, buildingSubtotal, specialEventsSubtotal,
      totalSpecialHours, subtotal, hst, total,
      buildingCount: activeBuildings.length,
      createdAt: existing?.createdAt || new Date().toISOString(),
      status: status || 'draft', notes,
      sentAt: existing?.sentAt || null,
      paidAt: existing?.paidAt || null,
    };
    const { error } = await supabase.from('invoices').upsert(toDbRow(invoice));
    if (error) {
      setInvoicesError(`Could not save invoice: ${error.message}`);
      return existing || null;
    }
    setInvoicesError(null);
    setSavedInvoices(prev => [invoice, ...prev.filter(i => i.id !== invoiceNumber)]);
    return invoice;
  }

  async function updateInvoiceStatus(invoiceId, status, extraFields) {
    const patch = {
      status,
      ...(extraFields?.sentAt ? { sent_at: extraFields.sentAt } : {}),
      ...(extraFields?.paidAt ? { paid_at: extraFields.paidAt } : {}),
    };
    const { error } = await supabase.from('invoices').update(patch).eq('id', invoiceId);
    if (error) {
      setInvoicesError(`Could not update invoice status: ${error.message}`);
      return;
    }
    setInvoicesError(null);
    setSavedInvoices(prev => prev.map(i => i.id === invoiceId ? { ...i, status, ...extraFields } : i));
  }

  function buildInvoiceHtml() {
    const buildingRows = activeBuildings.map(b =>
      `<tr><td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;font-size:13px">${b.name}</td>` +
      `<td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;font-size:13px;color:#64748b">${b.address}</td>` +
      `<td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;font-size:13px;text-align:right;font-weight:600">${formatCurrency(b.monthlyRate)}</td></tr>`
    ).join('');

    const eventRows = billedEvents.map(ev =>
      `<tr><td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;font-size:13px">${escapeHtml(ev.event_name)}</td>` +
      `<td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;font-size:13px;color:#64748b">${ev.event_date}</td>` +
      `<td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;font-size:13px;color:#64748b">${escapeHtml(ev.worker_name)}</td>` +
      `<td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;font-size:13px;text-align:right">${ev.hours_worked}</td>` +
      `<td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;font-size:13px;text-align:right;color:#64748b">${ev.billable ? (ev.event_type === 'full' ? 'Full-day' : 'Short') : 'Included (600hr allowance)'}</td>` +
      `<td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;font-size:13px;text-align:right;font-weight:600">${formatCurrency(ev.amount)}</td></tr>`
    ).join('');

    return `<div style="font-family:'Segoe UI',Arial,sans-serif;color:#1e293b;max-width:700px;margin:0 auto;padding:40px">` +
      `<table style="width:100%;margin-bottom:32px;border-bottom:2px solid #1e293b;padding-bottom:20px"><tr>` +
      `<td style="vertical-align:top"><div style="font-size:22px;font-weight:800">TC Janitorial Inc.</div>` +
      `<div style="font-size:12px;color:#64748b">Fabian Traverse Cleaning</div>` +
      `<div style="font-size:12px;color:#64748b">51 Quantum Drive, Paradise, NL A1L 0K1</div>` +
      `<div style="font-size:12px;color:#64748b">info@fabiantraverse.com &middot; (709) 771-2158</div></td>` +
      `<td style="text-align:right;vertical-align:top">` +
      `<div style="font-size:11px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:1px">INVOICE</div>` +
      `<div style="font-size:24px;font-weight:800;color:#f97316">${invoiceNumber}</div></td></tr></table>` +

      `<table style="width:100%;margin-bottom:24px"><tr>` +
      `<td style="vertical-align:top;width:50%">` +
      `<div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#94a3b8;margin-bottom:8px">Bill To</div>` +
      `<div style="font-size:13px;font-weight:600">Town of Paradise</div>` +
      `<div style="font-size:13px;color:#64748b">28 McNamara Drive, Paradise, NL A1L 0A6</div>` +
      `<div style="font-size:13px;color:#64748b">accountspayable@paradise.ca</div></td>` +
      `<td style="vertical-align:top;width:50%"><table style="width:100%">` +
      `<tr><td style="font-size:12px;color:#94a3b8;padding:4px 0">Contract</td><td style="font-size:13px;font-weight:600;text-align:right;padding:4px 0">${CONTRACT.number}</td></tr>` +
      `<tr><td style="font-size:12px;color:#94a3b8;padding:4px 0">PO Number</td><td style="font-size:13px;font-weight:600;text-align:right;padding:4px 0">${escapeHtml(poNumber) || '\u2014'}</td></tr>` +
      `<tr><td style="font-size:12px;color:#94a3b8;padding:4px 0">Period</td><td style="font-size:13px;font-weight:600;text-align:right;padding:4px 0">${formatDate(monthStart)} \u2013 ${formatDate(monthEnd)}</td></tr>` +
      `<tr><td style="font-size:12px;color:#94a3b8;padding:4px 0">Invoice Date</td><td style="font-size:13px;font-weight:600;text-align:right;padding:4px 0">${formatDate(new Date())}</td></tr>` +
      `<tr><td style="font-size:12px;color:#94a3b8;padding:4px 0">Authorized By</td><td style="font-size:13px;font-weight:600;text-align:right;padding:4px 0">${escapeHtml(authorizedBy)}</td></tr>` +
      `<tr><td style="font-size:12px;color:#94a3b8;padding:4px 0">Payment Terms</td><td style="font-size:13px;font-weight:600;text-align:right;padding:4px 0">Net 30</td></tr>` +
      `</table></td></tr></table>` +

      `<div style="font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;margin:24px 0 8px">Building Janitorial Services \u2014 ${MONTHS[selectedMonth]} ${selectedYear}</div>` +
      `<table style="width:100%;border-collapse:collapse">` +
      `<thead><tr style="background:#f8fafc">` +
      `<th style="text-align:left;padding:10px 12px;font-size:10px;font-weight:700;text-transform:uppercase;color:#94a3b8;border-bottom:2px solid #e2e8f0">Building</th>` +
      `<th style="text-align:left;padding:10px 12px;font-size:10px;font-weight:700;text-transform:uppercase;color:#94a3b8;border-bottom:2px solid #e2e8f0">Civic Address</th>` +
      `<th style="text-align:right;padding:10px 12px;font-size:10px;font-weight:700;text-transform:uppercase;color:#94a3b8;border-bottom:2px solid #e2e8f0">Monthly Rate</th>` +
      `</tr></thead><tbody>${buildingRows}` +
      `<tr style="background:#f8fafc;border-top:2px solid #e2e8f0">` +
      `<td colspan="2" style="padding:10px 12px;font-size:13px;font-weight:700">Building Services Subtotal</td>` +
      `<td style="padding:10px 12px;font-size:13px;font-weight:700;text-align:right">${formatCurrency(buildingSubtotal)}</td>` +
      `</tr></tbody></table>` +

      (specialEventHours.length > 0 ?
        `<div style="font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;margin:24px 0 8px">Special Event Hours \u2014 ${MONTHS[selectedMonth]} ${selectedYear}</div>` +
        `<div style="font-size:11px;color:#64748b;margin-bottom:8px">First ${SPECIAL_EVENTS_ALLOCATION} special-event hours per contract year (Apr\u2013Mar) are included in the base rate. Events past that allowance are billed at a flat $${SPECIAL_EVENTS_RATE_SHORT} (short) / $${SPECIAL_EVENTS_RATE_FULL} (full-day) rate, not per hour.</div>` +
        `<table style="width:100%;border-collapse:collapse"><thead><tr style="background:#f8fafc">` +
        `<th style="text-align:left;padding:10px 12px;font-size:10px;font-weight:700;text-transform:uppercase;color:#94a3b8;border-bottom:2px solid #e2e8f0">Event</th>` +
        `<th style="text-align:left;padding:10px 12px;font-size:10px;font-weight:700;text-transform:uppercase;color:#94a3b8;border-bottom:2px solid #e2e8f0">Date</th>` +
        `<th style="text-align:left;padding:10px 12px;font-size:10px;font-weight:700;text-transform:uppercase;color:#94a3b8;border-bottom:2px solid #e2e8f0">Worker</th>` +
        `<th style="text-align:right;padding:10px 12px;font-size:10px;font-weight:700;text-transform:uppercase;color:#94a3b8;border-bottom:2px solid #e2e8f0">Hours</th>` +
        `<th style="text-align:right;padding:10px 12px;font-size:10px;font-weight:700;text-transform:uppercase;color:#94a3b8;border-bottom:2px solid #e2e8f0">Billing</th>` +
        `<th style="text-align:right;padding:10px 12px;font-size:10px;font-weight:700;text-transform:uppercase;color:#94a3b8;border-bottom:2px solid #e2e8f0">Amount</th>` +
        `</tr></thead><tbody>${eventRows}` +
        `<tr style="background:#f8fafc;border-top:2px solid #e2e8f0">` +
        `<td colspan="3" style="padding:10px 12px;font-size:13px;font-weight:700">Special Events Subtotal</td>` +
        `<td style="padding:10px 12px;font-size:13px;font-weight:600;text-align:right">${totalSpecialHours} hrs</td>` +
        `<td style="padding:10px 12px;font-size:13px;font-weight:600;text-align:right">${billableEventCount} billable</td>` +
        `<td style="padding:10px 12px;font-size:13px;font-weight:700;text-align:right">${formatCurrency(specialEventsSubtotal)}</td>` +
        `</tr></tbody></table>`
        : `<div style="color:#94a3b8;font-size:13px;font-style:italic;padding:16px 0">No special event hours for ${MONTHS[selectedMonth]} ${selectedYear}.</div>`) +

      `<div style="background:#f8fafc;border-radius:8px;padding:20px;margin-top:24px">` +
      `<table style="width:100%">` +
      `<tr><td style="font-size:14px;color:#64748b;padding:4px 0">Building Services</td><td style="font-size:14px;color:#64748b;text-align:right;padding:4px 0">${formatCurrency(buildingSubtotal)}</td></tr>` +
      (specialEventsSubtotal > 0 ? `<tr><td style="font-size:14px;color:#64748b;padding:4px 0">Special Events (${totalSpecialHours} hrs)</td><td style="font-size:14px;color:#64748b;text-align:right;padding:4px 0">${formatCurrency(specialEventsSubtotal)}</td></tr>` : '') +
      `<tr><td colspan="2" style="padding:8px 0"><div style="height:1px;background:#e2e8f0"></div></td></tr>` +
      `<tr><td style="font-size:14px;color:#64748b;padding:4px 0">Subtotal</td><td style="font-size:14px;color:#64748b;text-align:right;padding:4px 0">${formatCurrency(subtotal)}</td></tr>` +
      `<tr><td style="font-size:14px;color:#64748b;padding:4px 0">HST (15%)</td><td style="font-size:14px;color:#64748b;text-align:right;padding:4px 0">${formatCurrency(hst)}</td></tr>` +
      `<tr><td colspan="2" style="padding:8px 0"><div style="height:1px;background:#e2e8f0"></div></td></tr>` +
      `<tr><td style="font-size:18px;font-weight:800;color:#1e293b;padding:4px 0">TOTAL DUE</td><td style="font-size:18px;font-weight:800;color:#f97316;text-align:right;padding:4px 0">${formatCurrency(total)}</td></tr>` +
      `</table></div>` +

      (notes ? `<div style="padding:16px 0;margin-top:16px;border-top:1px solid #e2e8f0"><div style="font-size:11px;text-transform:uppercase;color:#64748b;margin-bottom:4px;font-weight:600">Notes</div><div style="color:#64748b;font-size:13px">${escapeHtml(notes)}</div></div>` : '') +

      `<div style="margin-top:32px;padding-top:16px;border-top:1px solid #e2e8f0;color:#94a3b8;font-size:11px;text-align:center;line-height:1.8">` +
      `<div>Contract: TOP-25-27 \u2014 Provision of Janitorial Services: Town Buildings</div>` +
      `<div>Payment via EFT \u00b7 Net 30 terms</div></div></div>`;
  }

  async function sendInvoiceEmail() {
    setSending(true);
    setSendResult(null);
    try {
      const html = buildInvoiceHtml();
      const { error } = await supabase.functions.invoke('send-invoice', {
        body: {
          to: 'accountspayable@paradise.ca',
          subject: `Invoice ${invoiceNumber} \u2014 TC Janitorial Inc. \u2014 ${MONTHS[selectedMonth]} ${selectedYear}`,
          html: html,
        },
      });
      if (error) throw error;
      await saveInvoice('sent');
      await updateInvoiceStatus(invoiceNumber, 'sent', { sentAt: new Date().toISOString() });
      setSendResult({ success: true, message: 'Invoice sent to accountspayable@paradise.ca' });
    } catch (err) {
      console.error('Send failed:', err);
      setSendResult({ success: false, message: 'Failed to send. You can still print and email manually.' });
    }
    setSending(false);
  }

  // ─── SETUP VIEW ───
  if (view === 'setup') {
    return (
      <div style={s.page}>
        <div style={s.headerRow}>
          <div style={s.pageTitle}>Invoice Generator</div>
          <button style={s.historyBtn} onClick={() => setView('history')}>
            Invoice History ({savedInvoices.length})
          </button>
        </div>
        {invoicesError && (
          <div style={{ background: '#FBF1EF', border: '1px solid #B33A2E', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#B33A2E' }}>
            Invoice history error: {invoicesError}
          </div>
        )}
        <div style={s.card}>
          <div style={s.cardTitle}>Generate Monthly Invoice</div>
          <div style={s.cardSub}>
            Select a billing period. Building rates are locked from the contract.
            Special event hours are pulled automatically.
          </div>
          {(() => {
            const existing = savedInvoices.find(i => i.id === invoiceNumber);
            if (!existing || existing.status === 'draft') return null;
            return (
              <div style={{ background: 'rgba(31,58,71,0.08)', border: '1px solid #1F3A47', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#1F3A47' }}>
                Invoice {existing.id} for this month was already marked <strong>{existing.status}</strong>
                {existing.sentAt ? ` on ${new Date(existing.sentAt).toLocaleDateString('en-CA')}` : ''}.
                Re-previewing won't change that status.
              </div>
            );
          })()}
          <div style={s.formGrid}>
            <div style={s.field}>
              <label style={s.label}>Billing Month</label>
              <select style={s.select}
                value={`${selectedYear}-${selectedMonth}`}
                onChange={e => {
                  const parts = e.target.value.split('-').map(Number);
                  setSelectedYear(parts[0]);
                  setSelectedMonth(parts[1]);
                }}>
                {billableMonths.length > 0 ? billableMonths.map(bm => (
                  <option key={`${bm.year}-${bm.month}`} value={`${bm.year}-${bm.month}`}>{bm.label}</option>
                )) : (
                  <option value={`${defaultYear}-${defaultMonth}`}>{MONTHS[defaultMonth]} {defaultYear}</option>
                )}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>PO Number</label>
              <input style={s.input} value={poNumber} onChange={e => setPoNumber(e.target.value)}
                placeholder="Town's purchase order number" />
              {!poNumber && <div style={s.fieldHint}>Required — get from Jason/Charlene</div>}
            </div>
            <div style={s.field}>
              <label style={s.label}>Authorized By</label>
              <input style={s.input} value={authorizedBy} onChange={e => setAuthorizedBy(e.target.value)} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Notes (optional)</label>
              <input style={s.input} value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Any additional notes" />
            </div>
          </div>
          <div style={s.summaryBox}>
            <div style={s.summaryTitle}>{MONTHS[selectedMonth]} {selectedYear} — Summary</div>
            <div style={s.summaryRow}><span>Active Buildings</span><span style={s.summaryValue}>{activeBuildings.length}</span></div>
            <div style={s.summaryRow}><span>Building Services</span><span style={s.summaryValue}>{formatCurrency(buildingSubtotal)}</span></div>
            <div style={s.summaryRow}>
              <span>Special Events ({loadingEvents ? '...' : `${totalSpecialHours} hrs, ${billableEventCount} billable`})</span>
              <span style={s.summaryValue}>{loadingEvents ? '...' : formatCurrency(specialEventsSubtotal)}</span>
            </div>
            <div style={s.summaryDivider} />
            <div style={s.summaryRow}><span>Subtotal</span><span style={s.summaryValue}>{formatCurrency(subtotal)}</span></div>
            <div style={s.summaryRow}><span>HST (15%)</span><span style={s.summaryValue}>{formatCurrency(hst)}</span></div>
            <div style={{ ...s.summaryRow, fontWeight: 700, color: '#1A2B33', fontSize: 16, paddingTop: 8 }}>
              <span>TOTAL</span><span style={{ color: '#1A2B33' }}>{formatCurrency(total)}</span>
            </div>
          </div>
          <button style={s.primaryBtn} onClick={async () => { await saveInvoice('draft'); setView('preview'); }}>Preview Invoice →</button>
        </div>
      </div>
    );
  }

  // ─── HISTORY VIEW ───
  if (view === 'history') {
    return (
      <div style={s.page}>
        <div style={s.headerRow}>
          <div style={s.pageTitle}>Invoice History</div>
          <button style={s.backBtn} onClick={() => setView('setup')}>← Back</button>
        </div>
        {invoicesError && (
          <div style={{ background: '#FBF1EF', border: '1px solid #B33A2E', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#B33A2E' }}>
            Invoice history error: {invoicesError}
          </div>
        )}
        {savedInvoices.length === 0 ? (
          <div style={s.emptyState}><div style={{ color: '#5B6B72', fontSize: 14 }}>No invoices generated yet.</div></div>
        ) : (
          <div style={s.historyList}>
            {[...savedInvoices].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(inv => (
              <div key={inv.id} style={s.historyCard}>
                <div style={s.historyHeader}>
                  <div>
                    <div style={s.historyInvNum}>{inv.id}</div>
                    <div style={s.historyPeriod}>{inv.monthLabel}</div>
                  </div>
                  <div style={{
                    ...s.statusBadge,
                    background: inv.status === 'paid' ? 'rgba(47,122,79,0.10)' : inv.status === 'sent' ? 'rgba(31,58,71,0.08)' : 'rgba(91,107,114,0.08)',
                    color: inv.status === 'paid' ? '#2F7A4F' : inv.status === 'sent' ? '#1F3A47' : '#5B6B72',
                    borderColor: inv.status === 'paid' ? '#2F7A4F' : inv.status === 'sent' ? '#1F3A47' : '#E4E0D8',
                  }}>
                    {inv.status === 'paid' ? '✓ Paid' : inv.status === 'sent' ? '↗ Sent' : '◇ Draft'}
                  </div>
                </div>
                <div style={s.historyDetails}>
                  <span>{inv.buildingCount} buildings</span><span>·</span>
                  <span>{inv.totalSpecialHours || 0} event hrs</span><span>·</span>
                  <span style={{ color: '#1A2B33', fontWeight: 700 }}>{formatCurrency(inv.total)}</span>
                </div>
                <div style={s.historyActions}>
                  {inv.status === 'draft' && (
                    <button style={s.smallBtn} onClick={() => updateInvoiceStatus(inv.id, 'sent', { sentAt: new Date().toISOString() })}>Mark as Sent</button>
                  )}
                  {inv.status === 'sent' && (
                    <>
                      <button style={{ ...s.smallBtn, borderColor: '#2F7A4F', color: '#2F7A4F' }}
                        onClick={() => updateInvoiceStatus(inv.id, 'paid', { paidAt: new Date().toISOString() })}>Mark as Paid</button>
                      <div style={s.net30Note}>Net 30 — due by {
                        new Date(new Date(inv.sentAt).getTime() + 30*24*60*60*1000).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })
                      }</div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─── PREVIEW VIEW ───
  return (
    <div style={s.page}>
      <div style={s.headerRow} className="no-print">
        <button style={s.backBtn} onClick={() => setView('setup')}>← Back to Setup</button>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button style={s.secondaryBtn} onClick={() => {
            const w = window.open('', '_blank');
            if (!w) {
              alert('Your browser blocked the print popup. Allow popups for this site and try again.');
              return;
            }
            w.document.write('<html><head><title>Invoice ' + invoiceNumber + '</title>' +
              '<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:"Segoe UI",Arial,sans-serif}</style>' +
              '</head><body>' + buildInvoiceHtml() + '</body></html>');
            w.document.close();
            setTimeout(() => w.print(), 300);
          }}>Print / Save PDF</button>
          <button
            style={{ ...s.primaryBtn, width: 'auto', opacity: (sending || !poNumber.trim()) ? 0.6 : 1 }}
            disabled={sending || !poNumber.trim()}
            title={!poNumber.trim() ? 'PO Number is required before sending — go back to Setup and add it.' : undefined}
            onClick={() => {
              if (window.confirm('Send invoice ' + invoiceNumber + ' for ' + formatCurrency(total) + ' to accountspayable@paradise.ca?')) {
                sendInvoiceEmail();
              }
            }}>
            {sending ? 'Sending...' : 'Approve & Send to Town →'}
          </button>
        </div>
      </div>

      {!poNumber.trim() && (
        <div className="no-print" style={{ padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13, background: 'rgba(181,115,10,0.08)', border: '1px solid #B5730A', color: '#96600A' }}>
          PO Number is missing — get it from Jason/Charlene and add it in Setup before sending to the Town.
        </div>
      )}

      {sendResult && (
        <div style={{
          padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14, fontWeight: 600,
          background: sendResult.success ? 'rgba(47,122,79,0.10)' : '#FBF1EF',
          color: sendResult.success ? '#2F7A4F' : '#B33A2E',
          border: '1px solid ' + (sendResult.success ? '#2F7A4F' : '#B33A2E'),
        }}>
          {sendResult.success ? '✓ ' : '✗ '}{sendResult.message}
        </div>
      )}

      <div id="invoice-printable" style={s.invoiceWrap}
        dangerouslySetInnerHTML={{ __html: buildInvoiceHtml() }} />
    </div>
  );
}

const s = {
  page: { padding: 20, background: '#F5F3EF', minHeight: 'calc(100vh - 130px)' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 },
  pageTitle: { color: '#1A2B33', fontSize: 24, fontWeight: 800, letterSpacing: -0.5 },
  card: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 12, padding: 28, maxWidth: 720 },
  cardTitle: { color: '#1A2B33', fontSize: 18, fontWeight: 700, marginBottom: 6 },
  cardSub: { color: '#5B6B72', fontSize: 13, marginBottom: 24, lineHeight: 1.5 },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 },
  label: { color: '#5B6B72', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  input: { width: '100%', minWidth: 0, boxSizing: 'border-box', background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, padding: '10px 14px', color: '#1A2B33', fontSize: 14, outline: 'none' },
  select: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, padding: '10px 14px', color: '#1A2B33', fontSize: 14, outline: 'none', cursor: 'pointer' },
  fieldHint: { color: '#96600A', fontSize: 11, fontWeight: 500 },
  summaryBox: { background: '#F5F3EF', border: '1px solid #E4E0D8', borderRadius: 10, padding: 20, margin: '24px 0' },
  summaryTitle: { color: '#5B6B72', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14 },
  summaryRow: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', color: '#5B6B72', fontSize: 14 },
  summaryValue: { color: '#1A2B33', fontWeight: 600 },
  summaryDivider: { height: 1, background: '#E4E0D8', margin: '10px 0' },
  primaryBtn: { background: '#f97316', color: '#FFFFFF', border: 'none', borderRadius: 10, padding: '14px 28px', fontWeight: 800, fontSize: 15, cursor: 'pointer', width: '100%' },
  secondaryBtn: { background: 'transparent', color: '#5B6B72', border: '1px solid #E4E0D8', borderRadius: 10, padding: '10px 20px', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  backBtn: { background: 'transparent', color: '#5B6B72', border: 'none', fontSize: 14, cursor: 'pointer', fontWeight: 600, padding: 0 },
  historyBtn: { background: '#FFFFFF', color: '#5B6B72', border: '1px solid #E4E0D8', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  smallBtn: { background: 'transparent', color: '#5B6B72', border: '1px solid #E4E0D8', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  emptyState: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 12, padding: 40, textAlign: 'center' },
  historyList: { display: 'flex', flexDirection: 'column', gap: 12 },
  historyCard: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 10, padding: 18 },
  historyHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  historyInvNum: { color: '#1A2B33', fontSize: 16, fontWeight: 700 },
  historyPeriod: { color: '#5B6B72', fontSize: 13 },
  statusBadge: { border: '1px solid', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 },
  historyDetails: { display: 'flex', gap: 8, color: '#5B6B72', fontSize: 13, marginBottom: 10 },
  historyActions: { display: 'flex', gap: 10, alignItems: 'center' },
  net30Note: { color: '#5B6B72', fontSize: 11, fontStyle: 'italic' },
  invoiceWrap: { background: 'white', borderRadius: 12, marginTop: 10, overflow: 'hidden' },
};
